import React from 'react';
import { Card, CardBody, Col, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import MiniGauge from './MiniGauge';
import { formatNumber } from './formatters';

/**
 * Tarjeta de KPI "en vivo" estilizada con icono opcional, gauge opcional y
 * un badge de estado a la derecha. Reemplaza al KpiCard genérico para tener
 * más densidad visual en este dashboard de monitoreo.
 */
const LiveKpi = ({ icon, title, value, sub, color = '#3B82F6', badge, gauge }) => (
  <Card className="metric-card ai-perf-kpi h-100">
    <CardBody>
      <div className="d-flex align-items-start">
        {icon && (
          <div
            className="ai-perf-kpi__icon"
            style={{
              backgroundColor: `${color}20`,
              color,
              padding: 10,
              borderRadius: 8,
              width: 44,
              height: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
              marginRight: 12,
              flexShrink: 0,
            }}
          >
            <i className={icon} />
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="d-flex align-items-center justify-content-between">
            <h3
              className="mb-1"
              style={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: 500 }}
            >
              {title}
            </h3>
            {badge}
          </div>
          <div
            className="ai-perf-kpi__value"
            style={{ fontSize: 28, fontWeight: 700, color: 'var(--text-primary)' }}
          >
            {value}
          </div>
          {sub && (
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{sub}</div>
          )}
        </div>
        {gauge && (
          <div style={{ marginLeft: 12, flexShrink: 0 }}>{gauge}</div>
        )}
      </div>
    </CardBody>
  </Card>
);

const Badge = ({ tone = 'neutral', children, title }) => {
  const tones = {
    danger: { bg: '#FEE2E2', color: '#991B1B' },
    warning: { bg: '#FEF3C7', color: '#92400E' },
    ok: { bg: '#D1FAE5', color: '#065F46' },
    neutral: { bg: 'var(--surface-secondary)', color: 'var(--text-secondary)' },
  };
  const s = tones[tone] || tones.neutral;
  return (
    <span
      className="badge ai-perf-badge"
      title={title}
      style={{
        backgroundColor: s.bg,
        color: s.color,
        fontWeight: 600,
        fontSize: 10.5,
        padding: '3px 7px',
      }}
    >
      {children}
    </span>
  );
};

const QueueNowSection = ({ data, expectedWorkers = 4 }) => {
  const { t } = useTranslation();
  const q = data?.queue_now || {};
  const redisLen = Number(q.redis_len || 0);
  const dbQueued = Number(q.db_queued || 0);
  const running = Number(q.running || 0);
  const alive = Number(q.workers_alive || 0);

  const drift = dbQueued - redisLen;

  const driftBadge =
    drift !== 0 ? (
      <Badge
        tone={drift > 0 ? 'danger' : 'warning'}
        title={
          drift > 0
            ? t('db_queued > redis_len: hay jobs marcados queued en BD que no están en Redis')
            : t('redis_len > db_queued: hay jobs en Redis sin BD (probable carrera)')
        }
      >
        {drift > 0 ? `⚠ -${drift}` : `+${Math.abs(drift)} extra`}
      </Badge>
    ) : (
      <Badge tone="ok">{t('consistente')}</Badge>
    );

  const workersBadge =
    alive < expectedWorkers ? (
      <Badge tone="danger">{t('faltan {{n}}', { n: expectedWorkers - alive })}</Badge>
    ) : alive > expectedWorkers ? (
      <Badge tone="warning">+{alive - expectedWorkers} zombie?</Badge>
    ) : (
      <Badge tone="ok">OK</Badge>
    );

  return (
    <>
      <h5
        className="mb-3 ai-perf-section-title"
        style={{ fontSize: 14, color: 'var(--text-secondary)', fontWeight: 600 }}
      >
        <i className="fas fa-bolt me-2" style={{ color: '#10B981' }} />
        {t('Estado en vivo')}
      </h5>
      <Row className="g-3 mb-4">
        <Col xs="12" md="6" lg="3">
          <LiveKpi
            icon="fas fa-list"
            title={t('Cola Redis')}
            value={formatNumber(redisLen)}
            sub={t('jobs encolados a procesar')}
            color="#3B82F6"
            gauge={<MiniGauge value={redisLen} scaleMax={200} warning={50} danger={200} />}
          />
        </Col>

        <Col xs="12" md="6" lg="3">
          <LiveKpi
            icon="fas fa-database"
            title={t('En BD (queued)')}
            value={formatNumber(dbQueued)}
            sub={drift === 0 ? t('igual a Redis') : t('desfase {{sign}}{{n}} vs Redis', { sign: drift > 0 ? '+' : '', n: drift })}
            color="#8B5CF6"
            badge={driftBadge}
          />
        </Col>

        <Col xs="12" md="6" lg="3">
          <LiveKpi
            icon="fas fa-spinner ai-perf-spin-slow"
            title={t('Procesando ahora')}
            value={formatNumber(running)}
            sub={t('workers activos en jobs')}
            color="#F59E0B"
          />
        </Col>

        <Col xs="12" md="6" lg="3">
          <LiveKpi
            icon="fas fa-server"
            title={t('Workers vivos')}
            value={`${alive} / ${expectedWorkers}`}
            sub={t('esperados (configurable)')}
            color={alive >= expectedWorkers ? '#10B981' : '#DC2626'}
            badge={workersBadge}
          />
        </Col>
      </Row>
    </>
  );
};

export default QueueNowSection;
